import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Shield, 
  FileText,
  Lock,
  Eye,
  UserCheck,
  ChevronDown,
  Truck,
  CheckCircle,
  AlertTriangle,
  ExternalLink,
  Mail,
  Phone,
  MapPin,
  Clock,
  Database,
  Share2,
  Trash2,
  Edit3,
  HelpCircle,
  ArrowRight,
  Sparkles,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';

export default function TermoResponsabilidade() {
  const [expandedSection, setExpandedSection] = useState(null);
  const [scrollY, setScrollY] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleSection = (id) => {
    setExpandedSection(expandedSection === id ? null : id);
  };

  const sections = [
    {
      id: 'intro',
      title: "Nosso Compromisso com Você",
      icon: <Shield className="w-6 h-6" />,
      color: "brand",
      content: `A BoraFrete é uma plataforma de tecnologia que conecta embarcadores e transportadoras, facilitando a negociação e execução de fretes de forma segura e transparente.

Este Termo de Responsabilidade estabelece as diretrizes de privacidade, proteção de dados e responsabilidades entre a BoraFrete, seus usuários e parceiros, garantindo uma experiência confiável para todos.`
    },
    {
      id: 'dados',
      title: "1. Dados que Coletamos",
      icon: <Database className="w-6 h-6" />,
      color: "blue",
      subsections: [
        {
          title: "Dados Cadastrais",
          items: ["Nome completo", "E-mail", "Telefone", "CNPJ/CPF", "Endereço", "Dados bancários"]
        },
        {
          title: "Dados do Veículo (Motoristas)",
          items: ["Placa", "RNTRC", "CRLV", "Modelo e ano", "Capacidade de carga"]
        },
        {
          title: "Dados de Navegação",
          items: ["IP e localização", "Dados do dispositivo", "Cookies", "Histórico de acesso"]
        }
      ]
    },
    {
      id: 'uso',
      title: "2. Como Usamos seus Dados",
      icon: <Eye className="w-6 h-6" />,
      color: "purple",
      items: [
        "Viabilizar a conexão entre embarcadores e transportadoras",
        "Garantir a segurança das transações e prevenir fraudes",
        "Personalizar sua experiência na plataforma",
        "Cumprir obrigações legais e regulatórias (ANTT)",
        "Enviar comunicações sobre serviços e novidades",
        "Gerar estatísticas e melhorar nossos produtos"
      ]
    },
    {
      id: 'compartilhamento',
      title: "3. Compartilhamento de Dados",
      icon: <Share2 className="w-6 h-6" />,
      color: "amber",
      content: "Seus dados são compartilhados apenas quando necessário para a prestação dos serviços:",
      items: [
        "Entre embarcadores e transportadoras para fechamento de fretes",
        "Com parceiros de tecnologia (cloud, pagamentos, segurança)",
        "Com autoridades quando exigido por lei ou ordem judicial",
        "Em caso de fusão, aquisição ou venda da empresa"
      ],
      alert: "Nunca vendemos seus dados para terceiros para fins de marketing."
    },
    {
      id: 'seguranca',
      title: "4. Segurança da Informação",
      icon: <Lock className="w-6 h-6" />,
      color: "emerald",
      content: `Adotamos medidas técnicas e administrativas rigorosas para proteger seus dados:
      
• Criptografia SSL/TLS em todas as transmissões
• Armazenamento em servidores seguros (cloud AWS)
• Monitoramento contínuo contra acessos não autorizados
• Backups regulares e redundância de dados
• Treinamento de equipe em boas práticas de segurança

Nenhum sistema é 100% invulnerável. Em caso de incidente de segurança, você será notificado em até 72 horas conforme a LGPD.`
    },
    {
      id: 'direitos',
      title: "5. Seus Direitos (LGPD)",
      icon: <UserCheck className="w-6 h-6" />,
      color: "cyan",
      grid: [
        { icon: <Eye className="w-5 h-5" />, title: "Acesso", desc: "Saber quais dados temos sobre você" },
        { icon: <Edit3 className="w-5 h-5" />, title: "Correção", desc: "Corrigir dados incompletos ou desatualizados" },
        { icon: <Trash2 className="w-5 h-5" />, title: "Exclusão", desc: "Solicitar a eliminação dos seus dados" },
        { icon: <Share2 className="w-5 h-5" />, title: "Portabilidade", desc: "Receber seus dados em formato legível" },
        { icon: <AlertTriangle className="w-5 h-5" />, title: "Oposição", desc: "Discordar de determinados tratamentos" },
        { icon: <HelpCircle className="w-5 h-5" />, title: "Informação", desc: "Saber como e por que usamos seus dados" }
      ]
    },
    {
      id: 'retencao',
      title: "6. Retenção e Exclusão",
      icon: <Clock className="w-6 h-6" />,
      color: "slate",
      content: `Mantemos seus dados apenas pelo tempo necessário:

• Dados cadastrais: enquanto sua conta estiver ativa
• Dados de fretes: 5 anos (obrigação fiscal e legal)
• Dados de navegação: até 12 meses (anonimizados após)
• Backups: até 30 dias após exclusão solicitada

Você pode solicitar a exclusão da conta a qualquer momento. Alguns dados podem ser retidos para cumprimento de obrigações legais.`
    },
    {
      id: 'cookies',
      title: "7. Cookies e Tecnologias",
      icon: <FileText className="w-6 h-6" />,
      color: "orange",
      content: "Utilizamos cookies para melhorar sua experiência:",
      items: [
        "Essenciais: funcionamento básico da plataforma",
        "Analíticos: entender como você usa nossos serviços",
        "Marketing: personalizar comunicações (com consentimento)"
      ],
      alert: "Você pode gerenciar suas preferências de cookies nas configurações do navegador."
    },
    {
      id: 'atualizacoes',
      title: "8. Atualizações deste Termo",
      icon: <Edit3 className="w-6 h-6" />,
      color: "pink",
      content: `Podemos atualizar este Termo periodicamente para refletir mudanças em nossos serviços ou na legislação.

• Alterações significativas: notificação por e-mail com 30 dias de antecedência
• Alterações menores: aviso na plataforma com 15 dias de antecedência
• Continuidade de uso após atualização implica aceitação

Última atualização: 24 de março de 2026`
    }
  ];

  const getColorClasses = (color) => {
    const colors = {
      brand: "from-brand-500/20 to-purple-500/20 border-brand-500/30 text-brand-400",
      blue: "from-blue-500/20 to-cyan-500/20 border-blue-500/30 text-blue-400",
      purple: "from-purple-500/20 to-pink-500/20 border-purple-500/30 text-purple-400",
      amber: "from-amber-500/20 to-orange-500/20 border-amber-500/30 text-amber-400",
      emerald: "from-emerald-500/20 to-teal-500/20 border-emerald-500/30 text-emerald-400",
      cyan: "from-cyan-500/20 to-blue-500/20 border-cyan-500/30 text-cyan-400",
      slate: "from-slate-500/20 to-gray-500/20 border-slate-500/30 text-slate-400",
      orange: "from-orange-500/20 to-amber-500/20 border-orange-500/30 text-orange-400",
      pink: "from-pink-500/20 to-rose-500/20 border-pink-500/30 text-pink-400"
    };
    return colors[color] || colors.brand;
  };

  return (
    <div className="min-h-screen bg-[#050508] text-slate-200 font-sans antialiased overflow-x-hidden selection:bg-brand-500/30 selection:text-brand-200">
      
      {/* Dynamic Background */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_20%_20%,rgba(59,130,246,0.15)_0%,transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_80%,rgba(139,92,246,0.15)_0%,transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_50%,rgba(6,182,212,0.05)_0%,transparent_70%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      {/* Header */}
      <header 
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: scrollY > 50 ? 'rgba(5, 5, 8, 0.9)' : 'transparent',
          backdropFilter: scrollY > 50 ? 'blur(20px)' : 'none',
          borderBottom: scrollY > 50 ? '1px solid rgba(255,255,255,0.1)' : 'none'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-brand-500/50 transition-all duration-300 group-hover:scale-110">
                  <Truck className="w-5 h-5 text-white" />
                </div>
                <div className="absolute inset-0 bg-brand-500 rounded-xl blur-xl opacity-0 group-hover:opacity-50 transition-opacity duration-300" />
              </div>
              <div className="hidden sm:block">
                <span className="text-xl font-bold text-white tracking-tight">
                  Bora<span className="text-brand-400">Frete</span>
                </span>
                <span className="block text-[10px] text-slate-500 uppercase tracking-widest">Termo de Responsabilidade</span>
              </div>
            </Link>
            
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">
                <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
                <span>Vigente 2026</span>
              </div>
              
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 transition-colors"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
              
              <Link 
                to="/"
                className="hidden md:flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 transition-all text-sm font-medium"
              >
                <span>Voltar ao app</span>
                <ExternalLink className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
        
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-[#050508]/95 backdrop-blur-xl border-b border-white/10 p-4">
            <div className="flex flex-col gap-2">
              <Link to="/" className="flex items-center gap-2 px-4 py-3 rounded-lg bg-white/5 text-white">
                <Truck className="w-5 h-5" />
                <span>Voltar ao BoraFrete</span>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 backdrop-blur border border-white/10 mb-8 text-sm text-brand-400 shadow-2xl animate-[float_6s_ease-in-out_infinite]">
            <Sparkles className="w-4 h-4" />
            <span>Privacidade & Proteção de Dados</span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            <span className="bg-gradient-to-r from-brand-400 via-purple-400 to-blue-400 bg-clip-text text-transparent">
              Termo de Responsabilidade
            </span>
          </h1>
          
          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto leading-relaxed mb-8">
            Transparência total sobre como protegemos seus dados e sua privacidade 
            enquanto você move o Brasil conosco.
          </p>

          <div className="flex flex-wrap justify-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10">
              <CheckCircle className="w-4 h-4 text-emerald-400" />
              LGPD Compliant
            </span>
            <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10">Atualizado: 24/03/2026</span>
            <span className="px-4 py-2 rounded-full bg-white/5 border border-white/10">Versão 2.0</span>
          </div>
        </div>
      </section>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 space-y-4">
        
        {sections.map((section) => (
          <article 
            key={section.id}
            className={`group relative overflow-hidden rounded-2xl border backdrop-blur-xl transition-all duration-500 hover:scale-[1.01] bg-gradient-to-br ${getColorClasses(section.color)} ${expandedSection === section.id ? 'bg-opacity-30' : 'bg-opacity-10'}`}
          >
            <div className={`absolute inset-0 bg-gradient-to-r ${getColorClasses(section.color).split(' ')[0]} opacity-0 group-hover:opacity-10 transition-opacity duration-700`} />
            
            <button 
              onClick={() => toggleSection(section.id)}
              className="w-full p-6 sm:p-8 flex items-center justify-between text-left relative z-10"
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getColorClasses(section.color)} bg-opacity-30 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}>
                  {React.cloneElement(section.icon, { className: "w-6 h-6 text-white" })}
                </div>
                <h2 className="text-xl font-bold text-white group-hover:text-brand-200 transition-colors">
                  {section.title}
                </h2>
              </div>
              <div className={`w-10 h-10 rounded-full bg-white/5 flex items-center justify-center transition-all duration-300 ${expandedSection === section.id ? 'rotate-180 bg-white/10' : 'group-hover:bg-white/10'}`}>
                <ChevronDown className="w-5 h-5 text-slate-400" />
              </div>
            </button>

            <div className={`overflow-hidden transition-all duration-700 ${expandedSection === section.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'}`}>
              <div className="px-6 pb-6 sm:px-8 sm:pb-8 pt-2 space-y-6">
                
                {section.content && (
                  <div className="text-slate-300 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </div>
                )}

                {section.subsections && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {section.subsections.map((sub, i) => (
                      <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10">
                        <h4 className="text-white font-semibold mb-3 text-sm">{sub.title}</h4>
                        <ul className="space-y-2">
                          {sub.items.map((item, j) => (
                            <li key={j} className="flex items-center gap-2 text-slate-400 text-sm">
                              <span className="w-1.5 h-1.5 rounded-full bg-brand-400" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {section.items && !section.grid && (
                  <ul className="space-y-3">
                    {section.items.map((item, i) => (
                      <li key={i} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/10">
                        <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-300">{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.grid && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {section.grid.map((item, i) => (
                      <div key={i} className="p-4 rounded-xl bg-white/5 border border-white/10 text-center hover:bg-white/10 transition-colors group/item">
                        <div className="text-brand-400 mb-2 group-hover/item:scale-110 transition-transform flex justify-center">
                          {item.icon}
                        </div>
                        <h4 className="text-white font-semibold text-sm mb-1">{item.title}</h4>
                        <p className="text-slate-400 text-xs">{item.desc}</p>
                      </div>
                    ))}
                  </div>
                )}

                {section.alert && (
                  <div className="p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-300 text-sm flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <span>{section.alert}</span>
                  </div>
                )}

              </div>
            </div>
          </article>
        ))}

        {/* Contact Card */}
        <div className="mt-12 p-8 rounded-3xl bg-gradient-to-br from-brand-500/20 via-purple-500/20 to-blue-500/20 border border-brand-500/40 backdrop-blur-xl relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-500/0 via-white/10 to-brand-500/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />
          
          <div className="relative z-10 text-center">
            <h3 className="text-2xl font-bold text-white mb-4">Dúvidas sobre seus dados?</h3>
            <p className="text-slate-300 mb-8 max-w-lg mx-auto">
              Nosso encarregado de proteção de dados está à disposição para esclarecimentos 
              e solicitações relacionadas à LGPD.
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a 
                href="mailto:privacidade@borafrete.com.br"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 rounded-xl transition-all text-white font-semibold group/email"
              >
                <Mail className="w-5 h-5 group-hover/email:scale-110 transition-transform" />
                <span>privacidade@borafrete.com.br</span>
              </a>
              
              <a 
                href="tel:+5565981181997"
                className="inline-flex items-center gap-3 px-8 py-4 bg-brand-500/20 hover:bg-brand-500/30 border border-brand-500/30 rounded-xl transition-all text-brand-300 font-semibold"
              >
                <Phone className="w-5 h-5" />
                <span>(65) 98118-1997</span>
              </a>
            </div>
            
            <div className="mt-6 flex items-center justify-center gap-2 text-sm text-slate-500">
              <MapPin className="w-4 h-4" />
              <span>Cuiabá/MT • CNPJ: 65.852.223/0001-22</span>
            </div>
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
          <Link 
            to="/termo-seguranca-carga"
            className="flex items-center justify-between p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-brand-500/30 hover:bg-white/10 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Shield className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Proteção de Carga</h4>
                <p className="text-slate-400 text-sm">Termo de segurança de transporte</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-brand-400 group-hover:translate-x-1 transition-all" />
          </Link>
          
          <Link 
            to="/termos-de-uso"
            className="flex items-center justify-between p-6 rounded-2xl bg-white/5 border border-white/10 hover:border-brand-500/30 hover:bg-white/10 transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                <FileText className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h4 className="text-white font-semibold">Termos de Uso</h4>
                <p className="text-slate-400 text-sm">Regras gerais da plataforma</p>
              </div>
            </div>
            <ArrowRight className="w-5 h-5 text-slate-500 group-hover:text-brand-400 group-hover:translate-x-1 transition-all" />
          </Link>
        </div>

      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-black/60 backdrop-blur-xl py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand-500 to-purple-600 rounded-xl flex items-center justify-center">
                <Truck className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-lg font-bold text-white">Bora<span className="text-brand-400">Frete</span></span>
                <p className="text-xs text-slate-500">Tecnologia que move o Brasil 🇧🇷</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span>© 2026 BoraFrete</span>
              <span className="hidden sm:inline">•</span>
              <span className="hidden sm:inline">Todos os direitos reservados</span>
            </div>
            
            {/* Link único combinado - evita acúmulo no footer */}
            <Link 
              to="/termo-responsabilidade"
              className="flex items-center gap-2 px-4 py-2 rounded-full bg-brand-500/10 border border-brand-500/30 text-brand-400 hover:bg-brand-500/20 transition-all text-sm font-medium"
            >
              <Shield className="w-4 h-4" />
              <span>Privacidade & Proteção</span>
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            </Link>
          </div>
        </div>
      </footer>

      {/* CSS */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #050508;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
      `}</style>
    </div>
  );
}